import { useState } from "react";
import { Link } from "react-router-dom";
import { Share2, Copy, Check, Download } from "lucide-react";
import { toast } from "sonner";
import SocialShareButtons from "@/components/earthpulse/SocialShareButtons";

const SHARE_URL = "https://earthpulse.store/";
const OG_URL = "/og-image.jpg";
const SHARE_TEXT = "Earth Pulse — space weather, Earth's heartbeat, and your star alignment at birth. Daily guidance for sensitive souls.";

/**
 * Landing-page share block: shows the OG card inline and gives visitors
 * a one-tap way to share the page (Web Share on mobile → clipboard on
 * desktop) plus a "Download for Instagram Stories" link that grabs the
 * JPEG straight from /public.
 */
export default function SharePagePreview() {
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);

  const handleShare = async () => {
    setBusy(true);
    try {
      // Try Web Share with the image file first (best on iOS/Android)
      if (typeof navigator !== "undefined" && navigator.canShare) {
        try {
          const res = await fetch(OG_URL, { cache: "no-store" });
          const blob = await res.blob();
          const file = new File([blob], "earth-pulse.jpg", { type: "image/jpeg" });
          const shareData = { title: "Earth Pulse", text: SHARE_TEXT, url: SHARE_URL, files: [file] };
          if (navigator.canShare(shareData)) {
            await navigator.share(shareData);
            toast.success("Shared ✨");
            return;
          }
        } catch { /* fall through */ }

        // Try plain URL share (no file)
        const urlOnly = { title: "Earth Pulse", text: SHARE_TEXT, url: SHARE_URL };
        if (navigator.canShare(urlOnly)) {
          await navigator.share(urlOnly);
          toast.success("Shared ✨");
          return;
        }
      }

      // Desktop / unsupported — copy link + download the card
      await navigator.clipboard.writeText(SHARE_URL);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
      toast.success("Link copied", { description: "The image is downloading — post it to your Story." });
      triggerDownload();
    } catch (e) {
      toast.error("Couldn't share — try copying the link manually.");
    } finally {
      setBusy(false);
    }
  };

  const triggerDownload = async () => {
    try {
      const res = await fetch(OG_URL, { cache: "no-store" });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "earth-pulse.jpg";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch { /* silent */ }
  };

  const copyOnly = async () => {
    try {
      await navigator.clipboard.writeText(SHARE_URL);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
      toast.success("Link copied");
    } catch {
      toast.error("Couldn't copy — please copy the link manually.");
    }
  };

  return (
    <section
      className="relative border-t border-[color:var(--gold)]/10"
      style={{ background: "linear-gradient(180deg, transparent, rgba(212,162,76,0.03))" }}
      data-testid="share-page-section"
    >
      <div className="mx-auto max-w-[1100px] px-6 md:px-10 py-24 md:py-32">
        <div className="text-center max-w-2xl mx-auto">
          <div className="label-overline">Send it to someone who feels it</div>
          <h2 className="mt-6 font-serif-display text-[color:var(--parchment)] text-4xl md:text-5xl leading-[1.05] tracking-tight">
            A card worth <span className="font-serif-italic text-[color:var(--gold)]">sharing.</span>
          </h2>
          <p className="mt-6 font-serif-italic text-[color:var(--parchment-soft)] text-lg leading-relaxed">
            One friend at a time is how a quiet corner of the internet stays quiet — and stays real.
          </p>
        </div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
          {/* OG preview card */}
          <div className="md:col-span-7 stagger-in">
            <div
              className="rounded-2xl overflow-hidden border border-[color:var(--gold)]/25"
              style={{
                boxShadow: "0 24px 80px -30px rgba(212, 162, 76, 0.35), 0 6px 24px -12px rgba(0,0,0,0.6)",
              }}
            >
              <img
                src={OG_URL}
                alt="Earth Pulse share card — For the ones who feel it first."
                className="w-full h-auto block"
                data-testid="share-og-preview"
                loading="lazy"
              />
            </div>
            <p className="mt-4 label-overline text-[10px] text-center">
              Instagram · Twitter · iMessage · TikTok · WhatsApp — this is what your link looks like.
            </p>
          </div>

          {/* Actions */}
          <div className="md:col-span-5 stagger-in space-y-4" style={{ animationDelay: "80ms" }}>
            <button
              type="button"
              onClick={handleShare}
              disabled={busy}
              data-testid="share-page-btn"
              className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-[color:var(--gold)] hover:bg-[color:var(--gold-soft)] text-[color:var(--bg-base)] font-medium transition hover-lift disabled:opacity-60"
            >
              <Share2 size={16} />
              {busy ? "Preparing…" : "Share this page"}
            </button>

            <button
              type="button"
              onClick={copyOnly}
              data-testid="share-page-copy"
              className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full border border-[color:var(--gold)]/50 text-[color:var(--parchment)] hover:bg-[color:var(--gold)]/10 transition"
            >
              {copied ? <Check size={16} /> : <Copy size={16} />}
              {copied ? "Link copied" : "Copy link"}
            </button>

            <a
              href={OG_URL}
              download="earth-pulse.jpg"
              data-testid="share-page-download"
              className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full border border-[color:var(--rose-gold)]/50 text-[color:var(--parchment)] hover:bg-[color:var(--rose-gold)]/10 transition"
            >
              <Download size={16} />
              Download for Instagram Stories
            </a>

            <div className="pt-4">
              <div className="label-overline text-[10px] text-center mb-3 text-[color:var(--gold)]">
                Post it in one tap
              </div>
              <SocialShareButtons
                url={SHARE_URL}
                text={SHARE_TEXT}
                testIdPrefix="landing-share"
              />
            </div>

            <p className="mt-3 font-serif-italic text-[color:var(--text-muted)] text-xs text-center leading-relaxed">
              earthpulse.store · save the card to your camera roll, tag us if you post.
            </p>

            <p className="mt-6 text-center text-[color:var(--parchment-soft)] font-sans-body text-sm">
              Have your own line about Earth Pulse?{" "}
              <Link
                to="/testimonial"
                className="text-[color:var(--gold)] hover:underline"
                data-testid="share-testimonial-link"
              >
                Leave a whisper →
              </Link>
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
